'use client';
import type { NewsComments } from '@/types';

import { Box, Divider, Icon, Stack, Text } from '@/components';
import { useTranslation } from '@/hooks';
import Repeat from '@/svgs/repeat.svg';
import { fonts } from '@/theme';
import { jalaliDayjs, linkParser } from '@/utils';

const formatCommentDate = (createdAt: string): string =>
  jalaliDayjs(new Date(createdAt)).format('YYYY/MM/DD - HH:mm');

const UserComments: React.FC<{ comments: NewsComments[] }> = ({ comments }) => {
  const { t } = useTranslation('pages.blog');
  return (
    <>
      {comments.length > 0 ? (
        <>
          <Box>
            {comments?.map((comment, index) => {
              const isLast = index === comments.length - 1;
              return (
                <Box key={comment.id} mb="20px">
                  <Stack
                    direction="row"
                    gap={{ base: '6px', md: '10px' }}
                    align="center"
                  >
                    <Text fontSize={{ base: 'xs', md: 'md' }} color="#98D3BB">
                      {comment.created_by}
                    </Text>
                    {comment.created_at && (
                      <Text
                        fontSize={{ base: '2xs', md: 'xs' }}
                        color="gray.700"
                      >
                        {formatCommentDate(comment.created_at)}
                      </Text>
                    )}
                  </Stack>
                  <Stack
                    sx={{
                      '*': {
                        fontFamily: `${fonts.style.fontFamily} !important`,
                      },
                    }}
                    direction="column"
                    bg="gray.50"
                  >
                    <Text
                      textAlign="justify"
                      fontSize={{ base: 'xs', md: 'md' }}
                      color="text"
                      lineHeight={{ base: '25px', md: '37px' }}
                      mb="4px"
                    >
                      {linkParser(comment.content)}
                    </Text>
                  </Stack>
                  {comment.reply.length > 0 &&
                    comment.reply.map((reply) => {
                      if (!reply.content) {
                        return null;
                      }
                      return (
                        <Stack
                          key={reply.id}
                          mt="5"
                          direction="row"
                          gap="5"
                          align="flex-start"
                        >
                          <Icon as={Repeat} width={'37px'} height="37px" />
                          <Stack direction="column" gap="4" mt="4" flexGrow={1}>
                            <Text fontSize={{ base: 'xs', md: 'lg' }}>
                              {t('liaAdmin')}
                            </Text>
                            <Box
                              sx={{
                                '*': {
                                  fontFamily: `${fonts.style.fontFamily} !important`,
                                },
                              }}
                              borderRadius="xs"
                              px="23px"
                              pt={{ base: '9px', md: '34px' }}
                              pb={{ base: '12px', md: '43px' }}
                              bg="gray.50"
                            >
                              <Text
                                lineHeight={{ base: '25px', md: '37px' }}
                                textAlign="justify"
                                fontSize={{ base: 'xs', md: 'md' }}
                                color="text"
                              >
                                {linkParser(reply.content)}
                              </Text>
                            </Box>
                          </Stack>
                        </Stack>
                      );
                    })}
                  {!isLast && <Divider borderColor="primary.50" />}
                </Box>
              );
            })}
          </Box>
        </>
      ) : (
        <Text>{t('noCommentsForNew')}</Text>
      )}
    </>
  );
};
export default UserComments;
